import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import {
  assignLicenceToUser,
  assignModulesToLicence,
  createLicence,
  deleteLicence,
  getLicenceModuleIds,
  getLicences,
  getModules,
  getUsersWithLicences,
  updateLicence,
} from "./api";

export default function LicencesTab() {
  const [licences, setLicences] = useState([]);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [userLicenceMap, setUserLicenceMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLicence, setEditingLicence] = useState(null);
  const [licenceName, setLicenceName] = useState("");
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [assigningUserId, setAssigningUserId] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [licenceFilter, setLicenceFilter] = useState("all");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [licencesData, modulesData, usersData] = await Promise.all([
        getLicences(),
        getModules(),
        getUsersWithLicences(),
      ]);
      setLicences(Array.isArray(licencesData) ? licencesData : []);
      setModules(Array.isArray(modulesData) ? modulesData : []);
      const nextUsers = Array.isArray(usersData) ? usersData : [];
      setUsers(nextUsers);
      setUserLicenceMap(
        nextUsers.reduce((acc, user) => {
          acc[user.id] = user.licenceId ? String(user.licenceId) : "";
          return acc;
        }, {}),
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load licences.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateDialog = () => {
    setEditingLicence(null);
    setLicenceName("");
    setSelectedModuleIds([]);
    setDialogOpen(true);
  };

  const openEditDialog = (licence) => {
    setEditingLicence(licence);
    setLicenceName(licence.name);
    setSelectedModuleIds(getLicenceModuleIds(licence));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingLicence(null);
    setLicenceName("");
    setSelectedModuleIds([]);
  };

  const handleSaveLicence = async () => {
    if (!licenceName.trim()) {
      setError("Licence name is required.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      let licenceId = editingLicence?.id;

      if (editingLicence) {
        await updateLicence(editingLicence.id, licenceName.trim());
      } else {
        const created = await createLicence(licenceName.trim());
        licenceId = created?.id;
      }

      if (licenceId) {
        await assignModulesToLicence(licenceId, selectedModuleIds);
      }

      closeDialog();
      await loadData();
      setSuccess(editingLicence ? "Licence updated." : "Licence created.");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save licence.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLicence = async (licence) => {
    if (!window.confirm(`Delete licence "${licence.name}"?`)) return;

    setError("");
    try {
      await deleteLicence(licence.id);
      await loadData();
      setSuccess("Licence deleted.");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete licence.");
    }
  };

  const handleAssignLicence = async (userId, licenceId) => {
    const normalizedLicenceId = licenceId ? String(licenceId) : "";
    const previousLicenceId = userLicenceMap[userId] ?? "";

    setAssigningUserId(userId);
    setError("");
    setSuccess("");
    setUserLicenceMap((prev) => ({
      ...prev,
      [userId]: normalizedLicenceId,
    }));

    try {
      await assignLicenceToUser(userId, normalizedLicenceId || null);
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? {
                ...user,
                licenceId: normalizedLicenceId || null,
                licence:
                  licences.find(
                    (licence) => String(licence.id) === normalizedLicenceId,
                  ) || null,
              }
            : user,
        ),
      );
      setSuccess("User licence updated.");
    } catch (err) {
      setUserLicenceMap((prev) => ({
        ...prev,
        [userId]: previousLicenceId,
      }));
      setError(
        err?.response?.data?.message || "Failed to assign licence to user.",
      );
    } finally {
      setAssigningUserId(null);
    }
  };

  const getSelectedLicenceId = (userId) => userLicenceMap[userId] ?? "";

  const normalizedSearch = userSearch.trim().toLowerCase();
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      !normalizedSearch ||
      [user.name, user.username, user.email]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch),
        );

    const currentLicenceId = getSelectedLicenceId(user.id);
    const matchesLicence =
      licenceFilter === "all" ||
      (licenceFilter === "none" && !currentLicenceId) ||
      String(currentLicenceId) === licenceFilter;

    return matchesSearch && matchesLicence;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Licences</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
            >
              Add Licence
            </Button>
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f3f1fe" }}>
                  <TableCell>Licence</TableCell>
                  <TableCell>Modules</TableCell>
                  <TableCell align="right" width={100}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {licences.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No licences yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  licences.map((licence) => (
                    <TableRow key={licence.id} hover>
                      <TableCell>{licence.name}</TableCell>
                      <TableCell>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {(licence.modules || []).length ? (
                            licence.modules.map((module) => (
                              <Chip
                                key={module.id}
                                label={module.name}
                                size="small"
                                variant="outlined"
                              />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No modules
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(licence)}
                          aria-label="Edit licence"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteLicence(licence)}
                          aria-label="Delete licence"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12} md={7}>
          <Typography variant="h6" mb={2}>
            User Licence Assignment
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search by username or email…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    fontSize="small"
                    sx={{ mr: 1, color: "text.secondary" }}
                  />
                ),
                endAdornment: userSearch ? (
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setUserSearch("")}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null,
              }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="licence-filter-label">Filter by licence</InputLabel>
              <Select
                labelId="licence-filter-label"
                label="Filter by licence"
                value={licenceFilter}
                onChange={(e) => setLicenceFilter(e.target.value)}
              >
                <MenuItem value="all">All licences</MenuItem>
                <MenuItem value="none">No licence</MenuItem>
                {licences.map((licence) => (
                  <MenuItem key={licence.id} value={String(licence.id)}>
                    {licence.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f3f1fe" }}>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell width={220}>Licence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {users.length === 0
                        ? "No users found."
                        : "No users match your search or filter."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.name || user.username}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <InputLabel id={`licence-label-${user.id}`}>
                            Licence
                          </InputLabel>
                          <Select
                            labelId={`licence-label-${user.id}`}
                            label="Licence"
                            value={getSelectedLicenceId(user.id)}
                            disabled={assigningUserId === user.id}
                            onChange={(e) =>
                              handleAssignLicence(user.id, e.target.value)
                            }
                            renderValue={(selected) => {
                              if (!selected) return "None";
                              const licence = licences.find(
                                (item) => String(item.id) === String(selected),
                              );
                              return licence?.name || selected;
                            }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {licences.map((licence) => (
                              <MenuItem
                                key={licence.id}
                                value={String(licence.id)}
                              >
                                {licence.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLicence ? "Edit Licence" : "Add Licence"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Licence Name"
              value={licenceName}
              onChange={(e) => setLicenceName(e.target.value)}
              fullWidth
              autoFocus
            />

            {modules.length === 0 ? (
              <Alert severity="info">
                Create modules on the Modules tab first, then attach them here.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Modules</InputLabel>
                <Select
                  multiple
                  label="Modules"
                  value={selectedModuleIds}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedModuleIds(
                      typeof value === "string" ? value.split(",") : value,
                    );
                  }}
                  input={<OutlinedInput label="Modules" />}
                  renderValue={(selected) =>
                    selected
                      .map(
                        (id) =>
                          modules.find((module) => String(module.id) === id)
                            ?.name,
                      )
                      .filter(Boolean)
                      .join(", ") || "None"
                  }
                >
                  {modules.map((module) => (
                    <MenuItem key={module.id} value={String(module.id)}>
                      <Checkbox
                        checked={selectedModuleIds.includes(String(module.id))}
                      />
                      <ListItemText primary={module.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Typography variant="body2" color="text.secondary">
              Selected modules are linked to this licence when you save.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveLicence} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
